<template>
    <a-modal
        title="导入案例数据"
        v-model:open="open"
        :width="'520px'"
        :footer="null"
        :mask-closable="!importing"
        @cancel="handleCancel"
    >
        <div class="import-body">
            <!-- 上传区域 -->
            <div
                class="upload-zone"
                :class="{ 'upload-zone--dragover': isDragover, 'upload-zone--has-file': selectedFile }"
                @dragover.prevent="isDragover = true"
                @dragleave.prevent="isDragover = false"
                @drop.prevent="handleDrop"
                @click="triggerUpload"
            >
                <input
                    ref="fileInputRef"
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    style="display: none"
                    @change="handleFileChange"
                />
                <div v-if="!selectedFile" class="upload-placeholder">
                    <UploadOutlined class="upload-icon" />
                    <p class="upload-text">点击或拖拽文件到此处上传</p>
                    <p class="upload-hint">支持 .xlsx、.xls、.csv 格式</p>
                </div>
                <div v-else class="upload-file-info">
                    <FileExcelOutlined class="file-icon" />
                    <div class="file-details">
                        <span class="file-name">{{ selectedFile.name }}</span>
                        <span class="file-size">{{ formatSize(selectedFile.size) }}</span>
                    </div>
                    <a-button
                        type="text"
                        size="small"
                        danger
                        class="file-remove"
                        :disabled="importing"
                        @click.stop="removeFile"
                    >
                        <template #icon><DeleteOutlined /></template>
                    </a-button>
                </div>
            </div>

            <!-- 提示 -->
            <a-alert
                type="info"
                show-icon
                class="import-tip"
            >
                <template #message>
                    <span>请上传符合模板格式的 Excel 文件，<a href="#" @click.prevent="downloadTemplate">下载模板</a></span>
                </template>
            </a-alert>
        </div>

        <!-- 底部按钮 -->
        <div class="import-footer">
            <a-button @click="handleCancel" :disabled="importing">取消</a-button>
            <a-button
                type="primary"
                :loading="importing"
                :disabled="!selectedFile"
                @click="handleImport"
            >
                <template #icon><UploadOutlined /></template>
                开始导入
            </a-button>
        </div>
    </a-modal>
</template>

<script>
import { importTaxCase } from '@/api/service';
import { UploadOutlined, DeleteOutlined, FileExcelOutlined } from '@ant-design/icons-vue';
import { message } from 'ant-design-vue';
import { ref, h } from 'vue';

export default {
    name: 'importCaseComponent',
    setup(props, context) {
        const open = ref(false);
        const importing = ref(false);
        const selectedFile = ref(null);
        const fileInputRef = ref(null);
        const isDragover = ref(false);

        const openImport = () => {
            open.value = true;
            selectedFile.value = null;
            isDragover.value = false;
        };

        const handleCancel = () => {
            if (importing.value) return;
            open.value = false;
        };

        const triggerUpload = () => {
            if (importing.value) return;
            fileInputRef.value?.click();
        };

        const handleFileChange = (e) => {
            const file = e.target.files?.[0];
            if (file) validateAndSet(file);
            e.target.value = '';
        };

        const handleDrop = (e) => {
            isDragover.value = false;
            const file = e.dataTransfer?.files?.[0];
            if (file) validateAndSet(file);
        };

        const validateAndSet = (file) => {
            const allowed = ['.xlsx', '.xls', '.csv'];
            const ext = '.' + file.name.split('.').pop().toLowerCase();
            if (!allowed.includes(ext)) {
                message.error('不支持的文件格式，请上传 .xlsx、.xls 或 .csv 文件');
                return;
            }
            selectedFile.value = file;
        };

        const removeFile = () => {
            selectedFile.value = null;
        };

        const formatSize = (bytes) => {
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        };

        const downloadTemplate = () => {
            message.info('模板下载功能待对接');
        };

        const handleImport = async () => {
            if (!selectedFile.value) {
                message.warning('请先选择要导入的文件');
                return;
            }
            importing.value = true;
            try {
                const formData = new FormData();
                formData.append('file', selectedFile.value);
                const response = await importTaxCase(formData);
                if (response.code === 200) {
                    message.success('导入成功');
                    open.value = false;
                    context.emit('import-success');
                } else {
                    message.error('导入失败' + (response.msg || ''));
                }
            } catch (e) {
                message.error('导入失败：' + (e.response?.data?.msg || e.message));
            }
            importing.value = false;
        };

        return {
            open,
            importing,
            selectedFile,
            fileInputRef,
            isDragover,
            openImport,
            handleCancel,
            triggerUpload,
            handleFileChange,
            handleDrop,
            removeFile,
            formatSize,
            downloadTemplate,
            handleImport,
            h,
            UploadOutlined,
            DeleteOutlined,
            FileExcelOutlined,
        };
    },
};
</script>

<style scoped>
.import-body {
    padding: 8px 0;
}

.upload-zone {
    border: 2px dashed #d9d9d9;
    border-radius: 8px;
    padding: 40px 20px;
    text-align: center;
    cursor: pointer;
    transition: all 0.25s ease;
    background: #fafafa;
    margin-bottom: 16px;
}

.upload-zone:hover {
    border-color: #1890ff;
    background: #f0f8ff;
}

.upload-zone--dragover {
    border-color: #1890ff;
    background: #e6f7ff;
    box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.1);
}

.upload-zone--has-file {
    border-style: solid;
    border-color: #91d5ff;
    background: #fff;
    padding: 20px;
    cursor: default;
}

.upload-icon {
    font-size: 48px;
    color: #1890ff;
    margin-bottom: 12px;
}

.upload-text {
    font-size: 14px;
    color: #1a1a2e;
    margin-bottom: 6px;
    font-weight: 500;
}

.upload-hint {
    font-size: 12px;
    color: #8c8c8c;
    margin: 0;
}

.upload-file-info {
    display: flex;
    align-items: center;
    gap: 12px;
}

.file-icon {
    font-size: 32px;
    color: #52c41a;
    flex-shrink: 0;
}

.file-details {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
}

.file-name {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
    word-break: break-all;
}

.file-size {
    font-size: 12px;
    color: #8c8c8c;
}

.file-remove {
    flex-shrink: 0;
}

.import-tip {
    margin-bottom: 0;
}

.import-footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}
</style>
